/**
 * Settings Page
 * Contains various settings tabs, including user management for superadmins
 */

import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import Button from "../components/common/Button";
import Modal from "../components/common/Modal";
import UserForm from "../components/users/UserForm";
import UserList from "../components/users/UserList";
import ProfileSettings from "../components/settings/ProfileSettings";
import SecuritySettings from "../components/settings/SecuritySettings";
import MyCtaSettings from "../components/settings/MyCtaSettings";
import VoucherLocatorSettings from "../components/settings/VoucherLocatorSettings";

type Tab =
  | "user-management"
  | "general"
  | "my-cta"
  | "security"
  | "notifications"
  | "redemption-locator";

// Audit F2 (docs/AUDIT_PROMPT_PERSONAL_CTAS.md + PLAN_PERSONAL_CTA_ATTRIBUTION.md): hold the
// user-facing "My CTA" tab until sign-up attribution CAPTURE exists — a link shared before then
// loses credit forever. The model ships DORMANT. Phase-2 activation requires BOTH this flag AND
// a phase-2 migration that GRANTs EXECUTE on get_or_create_my_personal_cta to authenticated (the
// accessor is the ONLY read/write path — the tables themselves stay client-revoked, R4 F6). So
// flipping this flag alone does nothing until the accessor is re-granted. Partners stay excluded.
const PERSONAL_CTA_TAB_ENABLED = false;

const Settings: React.FC = () => {
  const { user, permissions } = useAuth();

  // Tab configuration
  const tabs = [
    {
      id: "user-management" as Tab,
      name: "User Management",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      ),
      visible: !!permissions?.can_manage_users,
    },
    {
      id: "general" as Tab,
      name: "General",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
      visible: true,
    },
    {
      id: "my-cta" as Tab,
      name: "My CTA",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5m6.828-1.828a4 4 0 000-5.656l-3-3a4 4 0 00-5.656 5.656l1.5 1.5"
          />
        </svg>
      ),
      // Hidden until attribution capture ships (F2). When re-enabled, partners stay excluded
      // (they attribute via their org); the accessor RPC also refuses partners server-side.
      visible:
        PERSONAL_CTA_TAB_ENABLED &&
        String(user?.role || "").toLowerCase() !== "partner",
    },
    {
      id: "security" as Tab,
      name: "Security",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
      ),
      visible: true,
    },
    {
      // Public Voucher Redemption Locator settings. RLS + the admin RPCs refuse everyone else.
      id: "redemption-locator" as Tab,
      name: "Redemption Locator",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
      visible: ["admin", "superadmin"].includes(
        String(user?.role || "").toLowerCase(),
      ),
    },
    {
      id: "notifications" as Tab,
      name: "Notifications",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
      ),
      visible: true,
    },
  ];

  const visibleTabs = tabs.filter((tab) => tab.visible);

  // Set default active tab based on user permissions
  const canManageUsers = !!permissions?.can_manage_users;
  const defaultTab = canManageUsers ? "user-management" : "general";
  const [activeTab, setActiveTab] = useState<Tab>(defaultTab);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  // Update active tab when permissions FIRST load. Only once: refreshUser() (e.g. after saving
  // the General profile form) produces a new permissions object, and re-running this bounced an
  // admin from General to User Management the moment they saved, hiding the success message.
  const defaultTabAppliedRef = React.useRef(false);
  React.useEffect(() => {
    if (!permissions || defaultTabAppliedRef.current) {
      return;
    }
    defaultTabAppliedRef.current = true;
    if (permissions.can_manage_users && activeTab === "general") {
      setActiveTab("user-management");
    }
  }, [permissions]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="mt-2 text-gray-600">
          Manage your account settings and preferences
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm
                ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              {tab.icon}
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow p-6">
        {/* User Management Tab */}
        {activeTab === "user-management" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  User Management
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  Invite new users and manage existing user roles and access
                </p>
              </div>
              <Button
                variant="primary"
                onClick={() => setIsInviteModalOpen(true)}
              >
                + Invite User
              </Button>
            </div>

            <UserList />
          </div>
        )}

        {/* General Tab */}
        {activeTab === "general" && <ProfileSettings />}

        {/* My CTA Tab */}
        {activeTab === "my-cta" && <MyCtaSettings />}

        {/* Redemption Locator Tab */}
        {activeTab === "redemption-locator" && <VoucherLocatorSettings />}

        {/* Security Tab */}
        {activeTab === "security" && <SecuritySettings />}

        {/* Notifications Tab */}
        {activeTab === "notifications" && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Notification Preferences
            </h2>
            <p className="text-gray-600">
              Email and notification preferences - Coming soon
            </p>
          </div>
        )}
      </div>

      {/* Invite User Modal */}
      <Modal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        title="Invite New User"
        size="md"
      >
        <UserForm
          onSuccess={() => setIsInviteModalOpen(false)}
          onCancel={() => setIsInviteModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export default Settings;
